import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BotMessageSquare,
  CalendarX,
  CheckCircle2,
  MessageSquare,
  RefreshCw,
  RefreshCcw,
  XCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { adminEventsApi } from '@/api';
import { useBusinessStore } from '@/stores';
// Direct imports to avoid circular dependency through the components barrel
import { AppSkeleton } from '../AppSkeleton';
import { colors, fonts, fontWeights, radius, spacing } from '@/theme/tokens';
import type { BusinessEventDTO, BusinessEventType } from '@tilly/shared';

dayjs.extend(relativeTime);

// ---------------------------------------------------------------------------
// Tilly-handled event types — these are actions completed automatically
// ---------------------------------------------------------------------------

const TILLY_EVENT_TYPES: BusinessEventType[] = [
  'message.sent',
  'payment.refunded',
  'appointment.completed',
  'appointment.no_show',
  'appointment.cancelled',
];

// ---------------------------------------------------------------------------
// Event icon
// ---------------------------------------------------------------------------

function ActivityIcon({ type, color }: { type: BusinessEventType; color: string }) {
  switch (type) {
    case 'message.sent':
      return <MessageSquare size={16} color={color} />;
    case 'payment.refunded':
      return <RefreshCcw size={16} color={color} />;
    case 'appointment.completed':
      return <CheckCircle2 size={16} color={color} />;
    case 'appointment.no_show':
      return <CalendarX size={16} color={color} />;
    case 'appointment.cancelled':
      return <XCircle size={16} color={color} />;
    default:
      return <BotMessageSquare size={16} color={color} />;
  }
}

// ---------------------------------------------------------------------------
// Map event to human-readable label
// ---------------------------------------------------------------------------

function getEventLabel(
  event: BusinessEventDTO,
  t: (key: string, options?: Record<string, unknown>) => string
): { title: string; description: string } {
  const payload = event.payload as Record<string, unknown>;
  const clientName = (payload.clientName as string) || (payload.name as string) || undefined;

  switch (event.type) {
    case 'message.sent': {
      const template = payload.templateId as string | undefined;
      const isReminder = template?.includes('reminder') || template?.includes('appointment');
      const isFollowUp = template?.includes('follow') || template?.includes('lapsed');
      if (isReminder) {
        return {
          title: t('admin:tillyActivityFeed.events.message.reminder'),
          description: clientName
            ? t('admin:tillyActivityFeed.events.message.sentTo', { name: clientName })
            : t('admin:tillyActivityFeed.events.message.sentGeneric'),
        };
      }
      if (isFollowUp) {
        return {
          title: t('admin:tillyActivityFeed.events.message.followUp'),
          description: clientName
            ? t('admin:tillyActivityFeed.events.message.sentTo', { name: clientName })
            : t('admin:tillyActivityFeed.events.message.sentGeneric'),
        };
      }
      return {
        title: t('admin:tillyActivityFeed.events.message.sent'),
        description: clientName
          ? t('admin:tillyActivityFeed.events.message.sentTo', { name: clientName })
          : t('admin:tillyActivityFeed.events.message.sentGeneric'),
      };
    }
    case 'payment.refunded': {
      const amountCents = payload.amountCents as number | undefined;
      const amountStr = amountCents != null ? `$${(amountCents / 100).toFixed(2)}` : undefined;
      return {
        title: t('admin:tillyActivityFeed.events.payment.refunded'),
        description: amountStr
          ? t('admin:tillyActivityFeed.events.payment.refundedAmount', { amount: amountStr })
          : clientName
            ? t('admin:tillyActivityFeed.events.payment.refundedTo', { name: clientName })
            : t('admin:tillyActivityFeed.events.payment.refundedGeneric'),
      };
    }
    case 'appointment.completed': {
      const serviceName = payload.serviceName as string | undefined;
      return {
        title: t('admin:tillyActivityFeed.events.appointment.completed'),
        description:
          clientName && serviceName
            ? t('admin:tillyActivityFeed.events.appointment.completedDetail', {
                name: clientName,
                service: serviceName,
              })
            : clientName
              ? t('admin:tillyActivityFeed.events.appointment.completedFor', { name: clientName })
              : t('admin:tillyActivityFeed.events.appointment.completedGeneric'),
      };
    }
    case 'appointment.no_show': {
      return {
        title: t('admin:tillyActivityFeed.events.appointment.noShow'),
        description: clientName
          ? t('admin:tillyActivityFeed.events.appointment.noShowFor', { name: clientName })
          : t('admin:tillyActivityFeed.events.appointment.noShowGeneric'),
      };
    }
    case 'appointment.cancelled': {
      return {
        title: t('admin:tillyActivityFeed.events.appointment.cancelled'),
        description: clientName
          ? t('admin:tillyActivityFeed.events.appointment.cancelledFor', { name: clientName })
          : t('admin:tillyActivityFeed.events.appointment.cancelledGeneric'),
      };
    }
    default:
      return {
        title: t('admin:tillyActivityFeed.events.default'),
        description: '',
      };
  }
}

// ---------------------------------------------------------------------------
// Contextual navigation per event type
// ---------------------------------------------------------------------------

function getEventNavPath(type: BusinessEventType): string {
  switch (type) {
    case 'message.sent':
      return '/admin/messages';
    case 'payment.refunded':
      return '/admin/settings?section=payments';
    case 'appointment.completed':
    case 'appointment.no_show':
    case 'appointment.cancelled':
      return '/admin/calendar';
    default:
      return '/admin/dashboard';
  }
}

// ---------------------------------------------------------------------------
// Activity row
// ---------------------------------------------------------------------------

function ActivityRow({ event, onNavigate }: { event: BusinessEventDTO; onNavigate: () => void }) {
  const { t: rawT } = useTranslation(['admin']);
  const t = (key: string, options?: Record<string, unknown>) =>
    rawT(key as never, options as never) as unknown as string;

  const { title, description } = getEventLabel(event, t);

  const iconColor =
    event.type === 'appointment.no_show' || event.type === 'appointment.cancelled'
      ? colors.textMuted
      : colors.accent;

  return (
    <button
      type="button"
      onClick={onNavigate}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[3],
        padding: `${spacing[3]} 0`,
        background: 'none',
        border: 'none',
        borderBottom: `1px solid ${colors.border}`,
        width: '100%',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {/* Icon */}
      <div
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: radius.full,
          backgroundColor: colors.surfaceRaised,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '1px',
        }}
      >
        <ActivityIcon type={event.type} color={iconColor} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: fonts.body,
            fontSize: '13px',
            fontWeight: fontWeights.semibold,
            color: colors.textPrimary,
            lineHeight: '18px',
          }}
        >
          {title}
        </div>
        {description ? (
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '12px',
              color: colors.textSecondary,
              lineHeight: '16px',
              marginTop: '1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {description}
          </div>
        ) : null}
      </div>

      {/* Timestamp */}
      <div
        style={{
          flexShrink: 0,
          fontFamily: fonts.body,
          fontSize: '11px',
          color: colors.textMuted,
          lineHeight: '18px',
        }}
      >
        {dayjs(event.timestamp).fromNow()}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TillyActivityFeedProps {
  maxVisible?: number;
}

export function TillyActivityFeed({ maxVisible = 5 }: TillyActivityFeedProps) {
  const { t: rawT } = useTranslation(['admin']);
  const t = (key: string, options?: Record<string, unknown>) =>
    rawT(key as never, options as never) as unknown as string;

  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const eventsQuery = useQuery({
    queryKey: ['tilly-activity', activeBusinessId],
    queryFn: () =>
      adminEventsApi.getEvents(activeBusinessId!, {
        types: TILLY_EVENT_TYPES,
        limit: 20,
      }),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const allEvents = eventsQuery.data?.events ?? [];
  const visibleEvents = expanded ? allEvents : allEvents.slice(0, maxVisible);
  const hiddenCount = Math.max(allEvents.length - maxVisible, 0);

  const isLoading = eventsQuery.isLoading;
  const isRefetching = eventsQuery.isRefetching;
  const isEmpty = !isLoading && allEvents.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Spin keyframe */}
      <style>{`
        @keyframes tilly-activity-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing[3],
        }}
      >
        <div>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '12px',
              fontWeight: fontWeights.semibold,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '2px',
            }}
          >
            {t('admin:tillyActivityFeed.title')}
          </div>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '14px',
              fontWeight: fontWeights.medium,
              color: colors.textPrimary,
            }}
          >
            {t('admin:tillyActivityFeed.subtitle')}
          </div>
        </div>

        <button
          onClick={() => void eventsQuery.refetch()}
          aria-label={t('admin:tillyActivityFeed.refresh')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: colors.textMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing[1],
            borderRadius: radius.md,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = colors.accent;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
          }}
        >
          <RefreshCw
            size={14}
            style={{
              animation: isRefetching ? 'tilly-activity-spin 1s linear infinite' : undefined,
            }}
          />
        </button>
      </div>

      {/* Body */}
      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[2] }}>
          {[0, 1, 2].map((i) => (
            <AppSkeleton key={i} height={44} />
          ))}
        </div>
      ) : isEmpty ? (
        <div
          style={{
            padding: `${spacing[8]} 0`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: spacing[2],
          }}
        >
          <div
            style={{
              fontFamily: fonts.body,
              fontWeight: fontWeights.semibold,
              fontSize: '15px',
              color: colors.textSecondary,
            }}
          >
            {t('admin:tillyActivityFeed.empty.title')}
          </div>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: '13px',
              color: colors.textMuted,
            }}
          >
            {t('admin:tillyActivityFeed.empty.description')}
          </div>
        </div>
      ) : (
        <div>
          {visibleEvents.map((event) => (
            <ActivityRow
              key={event.id}
              event={event}
              onNavigate={() => navigate(getEventNavPath(event.type))}
            />
          ))}

          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                fontSize: '13px',
                fontWeight: fontWeights.semibold,
                cursor: 'pointer',
                padding: `${spacing[2]} 0 0`,
                fontFamily: fonts.body,
              }}
            >
              {t('admin:tillyActivityFeed.showMore', { count: hiddenCount })}
            </button>
          )}

          {expanded && allEvents.length > maxVisible && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                fontSize: '13px',
                cursor: 'pointer',
                padding: `${spacing[2]} 0 0`,
                fontFamily: fonts.body,
              }}
            >
              {t('admin:tillyActivityFeed.showLess')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
